import {Component, inject, OnDestroy, OnInit} from '@angular/core';
import {NgbActiveModal} from "@ng-bootstrap/ng-bootstrap";
import {NgClass} from "@angular/common";
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {Subscription} from "rxjs";
import {
  FeedbackCommentOptionApiService,
  FeedbackCommentOptionStateService,
  FeedbackRateOptionApiService,
  FeedbackRateOptionStateService
} from "@numizmat/services/nomenclature";
import {FeedbackCommentOptionResponse, FeedbackRateOptionResponse} from "@numizmat/services-models/nomenclature";
import {DealParticipantType} from "@numizmat/enumerations";
import {UserFeedbackApiService} from "@numizmat/services/core";
import {UpsertUserFeedbackRequest, UpsertUserFeedbackResponse} from "@numizmat/services-models/core";
import {ToastrService} from "ngx-toastr";

@Component({
  selector: 'nz-user-feedback-modal',
  standalone: true,
  imports: [
    NgClass,
    FormsModule,
    ReactiveFormsModule
  ],
  templateUrl: './user-feedback-modal.component.html',
  styleUrl: './user-feedback-modal.component.scss'
})
export class UserFeedbackModalComponent implements OnInit, OnDestroy {
  private activeModal: NgbActiveModal = inject(NgbActiveModal);
  private toastrService: ToastrService = inject(ToastrService);
  private userFeedbackApiService: UserFeedbackApiService = inject(UserFeedbackApiService);
  private feedbackRateOptionApiService: FeedbackRateOptionApiService = inject(FeedbackRateOptionApiService);
  private feedbackRateOptionStateService: FeedbackRateOptionStateService = inject(FeedbackRateOptionStateService);
  private feedbackCommentOptionApiService: FeedbackCommentOptionApiService = inject(FeedbackCommentOptionApiService);
  private feedbackCommentOptionStateService: FeedbackCommentOptionStateService = inject(FeedbackCommentOptionStateService);

  private subs: Subscription = new Subscription();

  protected adId!: string;
  protected dealParticipantType!: DealParticipantType;

  protected errorMessage?: string;
  protected loadingData: boolean = false;
  protected loadingSubmit: boolean = false;
  protected feedbackRateOptions: FeedbackRateOptionResponse[] = [];
  protected feedbackCommentOptions: FeedbackCommentOptionResponse[] = [];
  protected filteredFeedbackCommentOptions: FeedbackCommentOptionResponse[] = [];

  protected form: FormGroup = this.initializeForm();

  protected readonly DealParticipantType = DealParticipantType;

  public ngOnInit(): void {
    this.getUserFeedbackDetails();
    this.getAllFeedbackRateOptions();
    this.getAllFeedbackCommentOption();
    this.createFeedbackRateChangeSubscription();
    this.createFeedbackCommentChangeSubscription();
  }

  public ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  protected onSubmit(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      return;
    }
    this.loadingSubmit = true;
    const request: UpsertUserFeedbackRequest = {
      adId: this.adId,
      feedbackRateOptionId: this.feedbackRateOptionId?.value,
      feedbackCommentOptionId: this.feedbackCommentOptionId?.value === -1 ? null : this.feedbackCommentOptionId?.value,
      customComment: this.customComment?.value
    };
    this.userFeedbackApiService.upsertUserFeedback(request).subscribe({
      next: (response: UpsertUserFeedbackResponse) => {
        this.loadingSubmit = false;
        if (response.success) {
          this.toastrService.success(response.successMessage);
          this.closeModal();
        } else {
          this.errorMessage = response.errorMessage;
        }
      }
    });
  }

  protected closeModal(): void {
    this.activeModal.dismiss();
  }

  private getUserFeedbackDetails() {
    this.userFeedbackApiService.getUserFeedbackDetails(this.adId).subscribe({
      next: response => {
        if (!!response) {
          this.feedbackRateOptionId?.setValue(response.feedbackRateOptionId);
          this.feedbackCommentOptionId?.setValue(!!response.feedbackCommentOptionId ? response.feedbackCommentOptionId : -1);
          this.customComment?.setValue(response.customComment);
        }
      }
    });
  }

  private getAllFeedbackRateOptions(): void {
    const subscription: Subscription = this.feedbackRateOptionStateService.getAllFeedbackRateOptions().subscribe({
      next: feedbackRateOptions => {
        if (!!feedbackRateOptions) {
          this.feedbackRateOptions = feedbackRateOptions;
        } else {
          this.feedbackRateOptionApiService.getAllFeedbackRateOptions().subscribe({
            next: response => {
              this.feedbackRateOptionStateService.setAllFeedbackRateOptions(response);
            }
          });
        }
      }
    });
    this.subs.add(subscription);
  }

  private getAllFeedbackCommentOption(): void {
    const subscription: Subscription = this.feedbackCommentOptionStateService.getAllFeedbackCommentOption().subscribe({
      next: feedbackCommentOptions => {
        if (!!feedbackCommentOptions) {
          this.feedbackCommentOptions = feedbackCommentOptions;
        } else {
          this.feedbackCommentOptionApiService.getAllFeedbackCommentOption().subscribe({
            next: response => {
              this.feedbackCommentOptionStateService.setAllFeedbackCommentOption(response);
            }
          });
        }
      }
    });
    this.subs.add(subscription);
  }

  private createFeedbackRateChangeSubscription(): void {
    const subscription: Subscription | undefined = this.feedbackRateOptionId?.valueChanges.subscribe({
      next: feedbackRateOptionId => {
        this.feedbackCommentOptionId?.setValue(null);
        this.customComment?.setValue(null);
        this.customComment?.disable();
        this.filteredFeedbackCommentOptions = this.feedbackCommentOptions.filter(x =>
          x.feedbackRateOptionId === feedbackRateOptionId &&
          x.dealParticipantType === this.dealParticipantType);

        this.filteredFeedbackCommentOptions.push({
          feedbackRateOptionId: feedbackRateOptionId,
          feedbackCommentOptionId: -1,
          feedbackCommentOptionComment: 'Собствен коментар',
          dealParticipantType: this.dealParticipantType
        });
      }
    });
    this.subs.add(subscription);
  }

  private createFeedbackCommentChangeSubscription(): void {
    const subscription: Subscription | undefined = this.feedbackCommentOptionId?.valueChanges.subscribe({
      next: feedbackCommentOptionId => {
        if (feedbackCommentOptionId !== -1) {
          this.customComment?.setValue(null);
          this.customComment?.disable();
        } else {
          this.customComment?.enable();
        }
      }
    });
    this.subs.add(subscription);
  }

  private initializeForm(): FormGroup {
    return new FormGroup({
      feedbackRateOptionId: new FormControl(null, [Validators.required]),
      feedbackCommentOptionId: new FormControl(null, [Validators.required]),
      customComment: new FormControl({value: null, disabled: true}, [Validators.required]),
    });
  }

  get feedbackRateOptionId() {
    return this.form.get('feedbackRateOptionId');
  }

  get feedbackCommentOptionId() {
    return this.form.get('feedbackCommentOptionId');
  }

  get customComment() {
    return this.form.get('customComment');
  }
}
